import { IntlShape } from 'react-intl';

import { MenuObjectTypes } from 'enums/menu';
import {
  ModifierTypes,
  ProductWizardStep,
  SingleOrMultipleModifierSelection,
} from 'state/product-wizard-v2/types';

import { IModifierSelections } from '../modifier/types';
import { getModifierPropsSelections } from '../product-customization/utils';
import { DEFAULT_SLOT_KEY } from '../product-detail-flow/product-item-flow/constants';

import { IGetModifierSelections } from './types';

export const getModifierSelections = ({
  isEdit,
  singleOrMultipleModifierSelection,
  modifierSelections,
  slotKeyToCustomize,
}: IGetModifierSelections) => {
  const slot = slotKeyToCustomize ? slotKeyToCustomize : DEFAULT_SLOT_KEY;
  const modifier = singleOrMultipleModifierSelection?.[slot];

  const hasModifier = !!modifier;
  const isModifierConfigured = !modifier?.isPristine || modifier?.isSaved || isEdit;
  const hasModifierSelections = !!modifierSelections;

  const shouldShowModifierSelections = hasModifierSelections && hasModifier && isModifierConfigured;

  return shouldShowModifierSelections
    ? getModifierPropsSelections(modifierSelections[modifier.key])
    : {};
};

interface IGetProductHomeCtaBtnProps {
  disabled: boolean;
  label: string;
}

interface IGetProductHomeCtaBtnPropsParams {
  selections: IModifierSelections;
  singleOrMultipleModifierSelection: SingleOrMultipleModifierSelection | null;
  currentStep?: ProductWizardStep;
  slotKeyToCustomize?: string;
  formatMessage: IntlShape['formatMessage'];
  type: MenuObjectTypes;
}

const getButtonLabel = (
  remainingSelections: number,
  modifierType: ModifierTypes | undefined,
  modifierSelection: any,
  formatMessage: IntlShape['formatMessage']
) => {
  const hasReachedMaxAmount = remainingSelections === modifierSelection?.maxAmount;
  // When user has already selected some items but not all
  if (remainingSelections && !hasReachedMaxAmount) {
    return modifierType === ModifierTypes.MULTIPLE_CHOICE
      ? formatMessage({ id: 'selectRemaining' }, { amount: remainingSelections })
      : formatMessage(
          { id: 'selectSingleChoice' },
          { amount: remainingSelections, itemName: modifierSelection?.name }
        );
  }

  // When user hasn't selected any items yet
  const itemName = `${formatMessage({ id: 'option' })}${remainingSelections > 1 ? 's' : ''}`;

  return formatMessage(
    { id: 'selectUpTo' },
    {
      amount: remainingSelections,
      itemName,
    }
  );
};

export const getSingleOrMultipleChoiceCtaBtnProps = ({
  selections,
  singleOrMultipleModifierSelection,
  currentStep,
  slotKeyToCustomize = DEFAULT_SLOT_KEY,
  formatMessage,
  type,
}: IGetProductHomeCtaBtnPropsParams): IGetProductHomeCtaBtnProps => {
  let disabled = false;
  let label = '';

  // There is no scenario where we should disable the CTA for a combo based on modifier selections.
  if (type === MenuObjectTypes.COMBO) {
    return { disabled, label };
  }

  const modifierSelection = singleOrMultipleModifierSelection?.[slotKeyToCustomize];
  const modifierType = modifierSelection?.type;

  const totalQuantitySelected = Object.values(selections ?? {}).reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const totalQuantityLessThanMaxAmount =
    totalQuantitySelected < (modifierSelection?.maxAmount || 0);

  // When user has made some selections but not all
  if (
    !!modifierSelection &&
    totalQuantityLessThanMaxAmount &&
    currentStep !== ProductWizardStep.ProductReview
  ) {
    disabled = true;

    const remainingSelections = (modifierSelection?.maxAmount || 0) - totalQuantitySelected;
    label = getButtonLabel(remainingSelections, modifierType, modifierSelection, formatMessage);
  }

  return { disabled, label };
};
